<template>
    <div class="contact-selector">
        <!-- Select existing contact or add new -->
        <div v-if="!read_only && available_contacts && available_contacts.length > 0" class="mb-4">
            <div class="flex items-center space-x-2">
                <select
                    v-model="selection_mode"
                    class="border border-gray-400 px-2 py-1 rounded text-sm"
                    @change="handleModeChange"
                >
                    <option value="select">{{ $t('Select existing contact') }}</option>
                    <option value="new">{{ $t('Add new contact') }}</option>
                </select>
            </div>
        </div>

        <!-- Select existing contact -->
        <div v-if="selection_mode === 'select' && !read_only && available_contacts && available_contacts.length > 0" class="mb-4">
            <input-label :for_element="field_id('contact_id')">
                {{ $t('Select Contact') }}
            </input-label>
            <select
                :id="field_id('contact_id')"
                :name="field_name('contact_id')"
                v-model="selected_contact_id"
                class="select-native border border-gray-400 px-2 rounded text-black w-full h-input"
                @change="handleContactSelect"
            >
                <option value="">{{ $t('Select a contact...') }}</option>
                <option
                    v-for="contact in filtered_contacts"
                    :key="contact.id"
                    :value="contact.id"
                >
                    {{ contact.name || $t('Unnamed') }}
                    <template v-if="contact.company"> - {{ contact.company }}</template>
                    <template v-if="contact.email"> ({{ contact.email }})</template>
                </option>
            </select>
        </div>

        <!-- Manual input fields (shown when adding new or no contacts available) -->
        <div v-if="selection_mode === 'new' || read_only || !available_contacts || available_contacts.length === 0" class="flex flex-col sm:flex-row">
            <div class="sm:col-gap-1 flex-1 my-1">
                <input-text
                    :name="field_name('name')"
                    v-model="contact_data.name"
                    :label="name_label || $t('Name')"
                    :placeholder="name_label || $t('Name')"
                    :read_only="read_only"
                    @input="handleManualInput"
                />
            </div>

            <div class="sm:col-gap-1 flex-1 my-1">
                <input-text
                    :name="field_name('email')"
                    v-model="contact_data.email"
                    label="Email"
                    placeholder="Email"
                    :read_only="read_only"
                    @input="handleManualInput"
                />
            </div>

            <div class="sm:col-gap-1 flex-1 my-1">
                <input-text
                    :name="field_name('phone')"
                    v-model="contact_data.phone"
                    :label="phone_label || $t('Phone')"
                    :placeholder="phone_label || $t('Phone')"
                    :read_only="read_only"
                    @input="handleManualInput"
                />
            </div>
        </div>

        <!-- Hidden field for contact_id when selecting existing -->
        <input
            v-if="selected_contact_id"
            type="hidden"
            :name="field_name('contact_id')"
            :value="selected_contact_id"
        />
    </div>
</template>

<script>
export default {
    props: {
        // Name prefix for form fields (e.g., 'attorney', 'inspector', 'closing_agent')
        name_prefix: { required: true },
        // Available contacts to select from (filtered by type)
        available_contacts: { default: () => [] },
        // Contact type to filter by
        contact_type: { default: null },
        // Current contact data (for editing)
        value: { default: () => ({}) },
        // Labels
        name_label: { default: null },
        phone_label: { default: null },
        // Read only mode
        read_only: { default: false },
    },

    data() {
        return {
            selection_mode: 'new', // 'select' or 'new'
            selected_contact_id: null,
            contact_data: {
                name: this.value.name || '',
                email: this.value.email || '',
                phone: this.value.phone || '',
            },
        }
    },

    computed: {
        filtered_contacts() {
            if (!this.available_contacts) return [];
            
            // Filter by type if specified
            if (this.contact_type) {
                return this.available_contacts.filter(contact => {
                    return contact.types && contact.types.includes(this.contact_type);
                });
            }
            
            return this.available_contacts;
        },
    },

    watch: {
        value: {
            deep: true,
            handler(newValue) {
                if (newValue) {
                    // If contact_id is set, switch to select mode
                    if (newValue.contact_id) {
                        this.selected_contact_id = newValue.contact_id;
                        this.selection_mode = 'select';
                    } else {
                        // Otherwise use manual input
                        this.contact_data = {
                            name: newValue.name || '',
                            email: newValue.email || '',
                            phone: newValue.phone || '',
                        };
                        this.selection_mode = 'new';
                    }
                }
            },
            immediate: true,
        },
    },

    methods: {
        field_name(field) {
            // Handle nested form fields like "contacts[buyer_attorney][name]"
            if (this.name_prefix.includes('[')) {
                return this.name_prefix + '[' + field + ']';
            }
            return this.name_prefix + '_' + field;
        },

        field_id(field) {
            // Generate a safe ID from the name
            return this.field_name(field).replace(/[\[\]]/g, '_');
        },

        handleModeChange() {
            if (this.selection_mode === 'select') {
                this.selected_contact_id = null;
                this.contact_data = { name: '', email: '', phone: '' };
            } else {
                this.selected_contact_id = null;
            }
            this.$emit('change', this.getOutput());
        },

        handleContactSelect() {
            if (this.selected_contact_id) {
                const contact = this.filtered_contacts.find(c => c.id == this.selected_contact_id);
                if (contact) {
                    this.contact_data = {
                        name: contact.name || '',
                        email: contact.email || '',
                        phone: contact.phone || '',
                    };
                }
            }
            this.$emit('change', this.getOutput());
        },

        handleManualInput() {
            this.selected_contact_id = null;
            this.$emit('change', this.getOutput());
        },

        getOutput() {
            if (this.selected_contact_id) {
                return {
                    contact_id: this.selected_contact_id,
                    name: this.contact_data.name,
                    email: this.contact_data.email,
                    phone: this.contact_data.phone,
                };
            }
            return {
                contact_id: null,
                name: this.contact_data.name,
                email: this.contact_data.email,
                phone: this.contact_data.phone,
            };
        },
    },
}
</script>

